import React from 'react';
import { Trans } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { integerToCurrency } from '@actual-app/core/shared/util';

import { Link } from '#components/common/Link';
import { cor, espaco, texto } from '#components/finapp/estilo';

export type ContaNaTela = {
  id: string;
  nome: string;
  /** `null` = o saldo ainda não chegou do spreadsheet (não é zero). */
  saldo: number | null;
  falhaDeSincronizacao: boolean;
};

/** Mesmo motivo de `inicio/ContasDoMes.tsx`: a serifa de 20px
 * (`texto.valorMedio`) é grande demais numa linha de 40px. Mantém
 * `tabular-nums` — a régua de todo valor da direção Extrato. */
const valorDaLinha = {
  ...texto.corpo,
  fontVariantNumeric: 'tabular-nums' as const,
};

const larguraColunaValor = 130;

type Props = {
  conta: ContaNaTela;
};

/**
 * Uma conta na tela Patrimônio: nome, marca de falha de sincronização e
 * saldo — a linha inteira é o link para `/accounts/:id`, que é onde o dono vê
 * o extrato e lança à mão.
 *
 * ⛔ SEM DIVISÓRIA entre linhas. O respiro vem da ALTURA
 * (`espaco.alturaLinha`, 40px), nunca de uma régua — regra nº 1 das 6
 * medidas no MULTICAP (borda repetida vira malha, e malha lê-se como
 * aperto). O realce de `:hover` usa o mesmo truque do cartão: contraste de
 * fundo, não contorno.
 */
export function LinhaDeConta({ conta }: Props) {
  const saldoNegativo = conta.saldo !== null && conta.saldo < 0;

  return (
    <Link
      variant="internal"
      to={`/accounts/${conta.id}`}
      style={{
        display: 'block',
        textDecoration: 'none',
        color: cor.tinta,
        borderRadius: espaco.raio,
        paddingLeft: 6,
        paddingRight: 6,
        marginLeft: -6,
        marginRight: -6,
        ':hover': { backgroundColor: cor.fundo },
      }}
    >
      <View
        data-testid={`conta-${conta.id}`}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
          height: espaco.alturaLinha,
        }}
      >
        <Text style={{ ...texto.corpo, flex: 1 }}>{conta.nome}</Text>

        {conta.falhaDeSincronizacao && (
          <Text
            data-testid={`conta-${conta.id}-falha`}
            style={{ ...texto.corpo, color: cor.saida }}
          >
            <Trans>⚠ reconectar banco</Trans>
          </Text>
        )}

        <Text
          data-testid={`conta-${conta.id}-saldo`}
          style={{
            ...valorDaLinha,
            width: larguraColunaValor,
            textAlign: 'right',
            color: saldoNegativo ? cor.saida : cor.tinta,
          }}
        >
          {conta.saldo === null
            ? '—'
            : `${saldoNegativo ? '-' : ''}${integerToCurrency(
                Math.abs(conta.saldo),
              )}`}
        </Text>
      </View>
    </Link>
  );
}
